/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown, LuisDropdownProps } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField, LuisTextFieldProps } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureSearchServiceNameAvailabilitySwagger, AzureSearchServiceSwagger } from 'src/core/services/azure/azureSwaggers';
import { AzureSearchServiceSku } from 'src/core/stores/AzureSearchStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { AzureResourcesManagerProps } from 'src/modules/azure/components/AzureResourcesManager';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';

type CreateAzureSearchResourceDto = { name: string; location: string; sku: string };

export type ServiceCreationModalProps = BasicModalProps<CreateAzureSearchResourceDto> & {
    onChangeAzureSearchService: (id: string) => void;
};

const messages = defineMessages({
    modalTitle: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.modalTitle',
        defaultMessage: 'Create Azure Search'
    },
    create: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.create',
        defaultMessage: 'Create'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.cancel',
        defaultMessage: 'Cancel'
    },
    modalDescription: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.modalDescription',
        defaultMessage: 'The knowledge base created from your content is stored in an Azure Search index in your Azure subscription.'
    },
    learnMore: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.learnMore',
        defaultMessage: 'Learn more about the best practices to select the right search service.'
    },
    nameLabel: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.nameLabel',
        defaultMessage: 'Azure Search Service name'
    },
    nameTooltip: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.nameTooltip',
        defaultMessage:
            'Service name must only contain lowercase letters, digits or dashes, cannot start or end with or contain consecutive dashes and is limited to 60 characters.'
    },
    namePlaceholder: { id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.namePlaceholder', defaultMessage: 'Type service name' },
    locationLabel: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.locationLabel',
        defaultMessage: 'Azure Search location'
    },
    locationLabelPlaceholder: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.locationLabelPlaceholder',
        defaultMessage: 'Select the location of the service'
    },
    skuLabel: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.skuLabel',
        defaultMessage: 'Azure Search pricing tier'
    },
    skuLabelPlaceholder: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.skuLabelPlaceholder',
        defaultMessage: 'Select the pricing tier of the service'
    },
    duplicateNameError: {
        id: 'qnaMaker.qnaMakerAzureSearchResourceCreationModal.duplicateNameError',
        defaultMessage: 'AlreadyExists'
    }
});

const { styleComponent } = getStylistV2('AzureSearchServiceCreationModal');
const StyledDropdown = styleComponent<LuisDropdownProps>(LuisDropdown)('StyledDropdown', { width: '300px' });
const StyledTextField = styleComponent<LuisTextFieldProps>(LuisTextField)('StyledTextField', { width: '300px' });
const LabelContainer = styleComponent<ILabelProps>(Label)('LabelContainer', { display: 'flex' });

/**
 * Represents the Azure Search Service creation modal.
 */
export const ServiceCreationModal = withModalMounter(
    observer((props: ServiceCreationModalProps) => {
        const { localize } = useLocalization();
        const { dispatcher, rootStore } = useGlobalState();
        const { isLargeOrGreater } = useResponsiveMode();

        const filteringStrategies: AzureResourcesManagerProps['strategies'] = {
            sku: sku => sku.kind === 'TextAnalytics',
            region: region => region.kind === 'TextAnalytics'
        };

        const { skus, regions, resourcesTracker } = useAzureResourcesManagement({
            skuStrategy: filteringStrategies.sku,
            regionStrategy: filteringStrategies.region,
            subscriptionId: rootStore.azureStore.session.subscriptionId,
            configs: { skuAndRegions: true }
        });

        const regionsOptions = regions.map(region => ({ key: region, text: region }));

        const skusOptions = skus.map(sku => ({ key: sku.code, text: `${sku.tier} (${sku.code})` }));

        const { formState, formStateDto, updateFormState } = useFormState<CreateAzureSearchResourceDto>({
            name: ['', FormValidators.createRequiredValidator()],
            location: ['', FormValidators.createRequiredValidator()],
            sku: ['', FormValidators.createRequiredValidator()]
        });

        const [isNameValid, setIsNameValid] = React.useState<AzureSearchServiceNameAvailabilitySwagger>();

        const isNameDuplicated =
            rootStore.azureStore.searchServicesStoreHouse.items.find(service => service.name === formState.fields.name.value) !== undefined;

        const azureSearchServiceNameAvailabilityTracker = useProgressTracker<AzureSearchServiceNameAvailabilitySwagger>({
            onComplete: nameAvailability => setIsNameValid(nameAvailability)
        });

        React.useEffect(() => {
            if (formState.fields.name.value.length > 0) {
                dispatcher.dispatch('checkAzureSearchResourceNameAvailability', {
                    id: rootStore.azureStore.session.subscriptionId,
                    name: formState.fields.name.value,
                    tracker: azureSearchServiceNameAvailabilityTracker
                });
            }
        }, [formState.fields.name.value]);

        const azureSearchServiceTracker = useProgressTracker<AzureSearchServiceSwagger>({
            onComplete: azureSearchService => {
                props.onDismiss();
                props.onChangeAzureSearchService(azureSearchService.id);
            }
        });

        const createAzureSearchService = () =>
            dispatcher.dispatch('createAzureSearchResource', {
                keyName: formStateDto.name,
                tracker: azureSearchServiceTracker,
                id: rootStore.azureStore.session.subscriptionId,
                keyProps: {
                    path: formStateDto.name,
                    id: formStateDto.name,
                    name: formStateDto.name,
                    location: formStateDto.location as Region,
                    sku: { name: skus.find(sku => sku.code === formStateDto.sku).tier as AzureSearchServiceSku['name'] }
                }
            });

        const onRenderFooter = () => (
            <Stack
                horizontal
                styles={{ root: { position: 'absolute', bottom: '0px', right: '0' } }}
                tokens={{ childrenGap: 8, padding: `0 ${isLargeOrGreater ? '32' : '0'} 0 0` }}>
                {azureSearchServiceNameAvailabilityTracker.isRunning ? (
                    <Spinner />
                ) : (
                    <PrimaryButton
                        disabled={!formState.form.valid || isNameDuplicated || !isNameValid?.nameAvailable}
                        onClick={createAzureSearchService}>
                        {localize(messages.create)}
                    </PrimaryButton>
                )}

                <DefaultButton disabled={azureSearchServiceNameAvailabilityTracker.isRunning} onClick={() => props.onDismiss()}>
                    {localize(messages.cancel)}
                </DefaultButton>
            </Stack>
        );

        const labelOnRender = (label: string, tooltipText?: string | React.ReactNode) => (
            <Stack horizontal verticalAlign="center">
                <LabelContainer styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required>
                    {label}
                </LabelContainer>
                <CalloutButton content={tooltipText} />
            </Stack>
        );

        return (
            <BasicModal
                {...props}
                minWidth={660}
                errorMessage={azureSearchServiceTracker.isFailed && azureSearchServiceTracker.error?.message}
                dialogContentProps={{ title: localize(messages.modalTitle) }}
                disabled={azureSearchServiceTracker.isRunning || props.disabled}
                onRenderFooter={onRenderFooter}>
                <Stack tokens={{ childrenGap: 12, padding: '0 0 19px 0' }}>
                    <LuisText>
                        {localize(messages.modalDescription)}
                        &nbsp;
                        <ExternalLink href="https://Aka.ms/cqa-search-best-practices">{localize(messages.learnMore)}</ExternalLink>
                    </LuisText>

                    <StyledTextField
                        autoFocus
                        value={formState.fields.name.value}
                        placeholder={localize(messages.namePlaceholder)}
                        onChange={(_, serviceName) => updateFormState({ name: serviceName })}
                        errorMessage={
                            isNameValid?.message ||
                            isNameValid?.reason ||
                            formState.fields.name.errorMessage ||
                            (isNameDuplicated && localize(messages.duplicateNameError))
                        }
                        onRenderLabel={() => labelOnRender(localize(messages.nameLabel), localize(messages.nameTooltip))}
                    />

                    <StyledDropdown
                        required
                        options={regionsOptions}
                        label={localize(messages.locationLabel)}
                        selectedKey={formState.fields.location.value}
                        isLoading={resourcesTracker.isRunning}
                        placeholder={localize(messages.locationLabelPlaceholder)}
                        onChange={(_, o) => updateFormState({ location: o.key as string })}
                    />

                    <StyledDropdown
                        required
                        options={skusOptions}
                        label={localize(messages.skuLabel)}
                        selectedKey={formState.fields.sku.value}
                        isLoading={resourcesTracker.isRunning}
                        placeholder={localize(messages.skuLabelPlaceholder)}
                        onChange={(_, o) => updateFormState({ sku: o.key as string })}
                    />
                </Stack>
            </BasicModal>
        );
    })
);
